package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.dto.courses.CourseAssignmentDto
import com.evoapps.evoshift.data.dto.courses.CourseCompletionDto
import com.evoapps.evoshift.data.dto.courses.CourseDto
import com.evoapps.evoshift.data.dto.courses.CourseKeyDto
import com.evoapps.evoshift.data.dto.courses.CourseProgressDto
import com.evoapps.evoshift.data.dto.courses.OhsRecordDto
import com.evoapps.evoshift.data.dto.courses.TrainingStatusDto
import com.evoapps.evoshift.data.mapper.CourseMapper
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.data.remote.FirebaseStorageClient
import com.evoapps.evoshift.domain.model.Course
import com.evoapps.evoshift.domain.model.CourseActionException
import com.evoapps.evoshift.domain.model.CourseAssignment
import com.evoapps.evoshift.domain.model.CourseCompletion
import com.evoapps.evoshift.domain.model.CourseFailure
import com.evoapps.evoshift.domain.model.CourseFiles
import com.evoapps.evoshift.domain.model.CourseKey
import com.evoapps.evoshift.domain.model.CourseProgress
import com.evoapps.evoshift.domain.model.CourseResult
import com.evoapps.evoshift.domain.model.CourseStatus
import com.evoapps.evoshift.domain.model.NotFoundException
import com.evoapps.evoshift.domain.model.OhsConfirmation
import com.evoapps.evoshift.domain.model.OhsEntry
import com.evoapps.evoshift.domain.model.OhsRecord
import com.evoapps.evoshift.domain.model.PresentationFiles
import com.evoapps.evoshift.domain.model.TrainingStatus
import com.evoapps.evoshift.domain.repository.CourseRepository
import com.evoapps.evoshift.domain.repository.OhsDocumentKind
import com.evoapps.evoshift.domain.repository.OhsDocumentLink
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.add
import kotlinx.serialization.json.decodeFromJsonElement
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonObject

/**
 * SH-126: courses, keys, assignments and progress in Firestore (firestore.rules), pictures in
 * Storage (storage.rules), grading and the OHS record through callables (functions/courses.js,
 * functions/ohsDocuments.js), which alone write results and records.
 */
class CourseRepositoryImpl(
    private val firestore: AppFirestore,
    private val storage: FirebaseStorageClient,
    private val functions: CloudFunctionsClient
) : CourseRepository {

    private fun workplace(wpId: String) = firestore.collection("workplaces").document(wpId)
    private fun courses(wpId: String) = workplace(wpId).collection("courses")
    private fun keys(wpId: String) = workplace(wpId).collection("course_keys")
    private fun progress(wpId: String) = workplace(wpId).collection("course_progress")
    private fun assignments(wpId: String, courseId: String) = courses(wpId).document(courseId).collection("assignments")
    private fun completions(wpId: String, courseId: String) = courses(wpId).document(courseId).collection("completions")

    override suspend fun getCourses(workplaceId: String, includeDrafts: Boolean): Result<List<Course>> = runCatching {
        // Members may list published courses only (the query must say so for the rules).
        val query = if (includeDrafts) courses(workplaceId).get() else courses(workplaceId).where { "status" equalTo CourseStatus.PUBLISHED.name }.get()
        query.documents.map { CourseMapper.toDomain(it.data<CourseDto>(), it.id, workplaceId) }
    }

    override suspend fun getCourse(workplaceId: String, courseId: String): Result<Course> = runCatching {
        val snap = courses(workplaceId).document(courseId).get()
        if (!snap.exists) throw NotFoundException("Course $courseId")
        CourseMapper.toDomain(snap.data<CourseDto>(), courseId, workplaceId)
    }

    override suspend fun getKey(workplaceId: String, courseId: String): Result<CourseKey?> = runCatching {
        keys(workplaceId).document(courseId).get().takeIf { it.exists }?.let { CourseMapper.toDomain(it.data<CourseKeyDto>(), courseId) }
    }

    override suspend fun saveCourse(course: Course, key: CourseKey): Result<Course> = runCatching {
        val stored = course.copy(id = course.id.ifBlank { newCourseId(course.workplaceId) })
        val batch = firestore.batch()
        batch.set(courses(stored.workplaceId).document(stored.id), CourseMapper.toDto(stored))
        batch.set(keys(stored.workplaceId).document(stored.id), CourseMapper.toDto(key.copy(courseId = stored.id), stored.updatedBy, stored.updatedAt))
        batch.commit()
        stored
    }

    override suspend fun deleteCourse(workplaceId: String, courseId: String): Result<Unit> = runCatching {
        val course = courses(workplaceId).document(courseId).get().takeIf { it.exists }?.data<CourseDto>()
        val refs = completions(workplaceId, courseId).get().documents.map { it.reference } +
            assignments(workplaceId, courseId).get().documents.map { it.reference } +
            progress(workplaceId).where { "courseId" equalTo courseId }.get().documents.map { it.reference }
        refs.chunked(400).forEach { chunk ->
            val batch = firestore.batch()
            chunk.forEach { batch.delete(it) }
            batch.commit()
        }
        keys(workplaceId).document(courseId).delete()
        courses(workplaceId).document(courseId).delete()
        course?.pages?.flatMap { page ->
            listOfNotNull(page.imagePath) + listOfNotNull(page.presentation?.filePath, page.presentation?.pdfPath)
        }?.forEach { path -> runCatching { storage.delete(path) } }
    }

    override suspend fun uploadImage(workplaceId: String, courseId: String, bytes: ByteArray, contentType: String): Result<String> = runCatching {
        val path = CourseFiles.imagePath(workplaceId, courseId, keys(workplaceId).document.id)
        storage.upload(path, bytes, contentType)
        path
    }

    override suspend fun downloadImage(path: String): Result<ByteArray> = runCatching {
        storage.download(storage.downloadUrl(path, null))
    }

    override suspend fun uploadPresentation(workplaceId: String, courseId: String, bytes: ByteArray, contentType: String): Result<String> = runCatching {
        val path = PresentationFiles.path(workplaceId, courseId, keys(workplaceId).document.id)
        storage.upload(path, bytes, contentType)
        path
    }

    override suspend fun downloadPresentation(path: String): Result<ByteArray> = runCatching {
        storage.download(storage.downloadUrl(path, null))
    }

    override fun newCourseId(workplaceId: String): String = courses(workplaceId).document.id

    override suspend fun getAssignments(workplaceId: String, courseId: String): Result<List<CourseAssignment>> = runCatching {
        assignments(workplaceId, courseId).get().documents.map { CourseMapper.toDomain(it.data<CourseAssignmentDto>(), courseId, it.id) }
    }

    override suspend fun getMyAssignments(workplaceId: String, userId: String, courseIds: List<String>): Result<List<CourseAssignment>> = runCatching {
        courseIds.mapNotNull { courseId ->
            assignments(workplaceId, courseId).document(userId).get().takeIf { it.exists }
                ?.let { CourseMapper.toDomain(it.data<CourseAssignmentDto>(), courseId, userId) }
        }
    }

    override suspend fun assign(workplaceId: String, assignment: CourseAssignment): Result<Unit> = runCatching {
        assignments(workplaceId, assignment.courseId).document(assignment.userId).set(CourseMapper.toDto(assignment))
    }

    override suspend fun unassign(workplaceId: String, courseId: String, userId: String): Result<Unit> = runCatching {
        assignments(workplaceId, courseId).document(userId).delete()
    }

    override suspend fun getCompletions(workplaceId: String, courseId: String): Result<List<CourseCompletion>> = runCatching {
        completions(workplaceId, courseId).get().documents.map { CourseMapper.toDomain(it.data<CourseCompletionDto>(), courseId, it.id) }
    }

    override suspend fun getCompletion(workplaceId: String, courseId: String, userId: String): Result<CourseCompletion?> = runCatching {
        completions(workplaceId, courseId).document(userId).get().takeIf { it.exists }
            ?.let { CourseMapper.toDomain(it.data<CourseCompletionDto>(), courseId, userId) }
    }

    override suspend fun getProgress(workplaceId: String, courseId: String, userId: String): Result<CourseProgress?> = runCatching {
        progress(workplaceId).document("${userId}_$courseId").get().takeIf { it.exists }
            ?.let { CourseMapper.toDomain(it.data<CourseProgressDto>()) }
    }

    override suspend fun getAllProgress(workplaceId: String, courseId: String): Result<List<CourseProgress>> = runCatching {
        progress(workplaceId).where { "courseId" equalTo courseId }.get().documents.map { CourseMapper.toDomain(it.data<CourseProgressDto>()) }
    }

    override suspend fun saveProgress(workplaceId: String, progress: CourseProgress): Result<Unit> = runCatching {
        progress(workplaceId).document("${progress.userId}_${progress.courseId}").set(CourseMapper.toDto(progress, workplaceId))
    }

    override suspend fun submit(workplaceId: String, courseId: String, version: Int, answers: Map<String, List<Int>>, confirm: Boolean): Result<CourseResult> = callable {
        val result = functions.call("submitCourse", buildJsonObject {
            put("workplaceId", workplaceId)
            put("courseId", courseId)
            put("version", version)
            put("confirm", confirm)
            putJsonObject("answers") {
                answers.forEach { (id, picked) -> put(id, buildJsonArray { picked.forEach { add(it) } }) }
            }
        }).jsonObject
        val completion = result["completion"]?.let { decode<CourseCompletionDto>(it) } ?: error("submitCourse answered no result")
        CourseResult(
            completion = CourseMapper.toDomain(completion, courseId, completion.userId),
            wrongQuestionIds = result["wrongQuestionIds"]?.takeIf { it !is JsonNull }?.jsonArray?.mapNotNull { it.jsonPrimitive.contentOrNull }.orEmpty(),
            ohsRecord = result["ohsRecord"]?.takeIf { it !is JsonNull }?.let { record(it, workplaceId) }
        )
    }

    override suspend fun getOhsRecord(workplaceId: String, userId: String): Result<OhsRecord?> = runCatching {
        workplace(workplaceId).collection("ohs_records").document(userId).get().takeIf { it.exists }
            ?.let { CourseMapper.toDomain(it.data<OhsRecordDto>(), userId, workplaceId) }
    }

    override suspend fun recordOhs(workplaceId: String, userId: String, entry: OhsEntry): Result<OhsRecord> = callable {
        val result = functions.call("recordOhsTraining", buildJsonObject {
            put("workplaceId", workplaceId)
            put("userId", userId)
            when (entry) {
                is OhsEntry.Settings -> {
                    put("part", "SETTINGS")
                    put("group", entry.group.name)
                    put("stationRequired", entry.stationRequired)
                }
                is OhsEntry.GeneralInPerson -> {
                    put("part", "GENERAL_IN_PERSON")
                    put("conductedBy", entry.conductedBy)
                    put("completedOn", entry.completedOn.toString())
                }
                is OhsEntry.Station -> {
                    put("part", "STATION")
                    put("position", entry.position)
                    put("conductedBy", entry.conductedBy)
                    put("conductedOn", entry.conductedOn.toString())
                    put("admittedOn", entry.admittedOn.toString())
                }
                is OhsEntry.PeriodicExam -> {
                    put("part", "PERIODIC_EXAM")
                    put("examOn", entry.examOn.toString())
                    put("commission", entry.commission)
                    if (entry.certificateNumber.isNotBlank()) put("certificateNumber", entry.certificateNumber)
                }
                is OhsEntry.PeriodicElsewhere -> {
                    put("part", "PERIODIC_ELSEWHERE")
                    put("examOn", entry.examOn.toString())
                    put("organizer", entry.organizer)
                    put("certificateNumber", entry.certificateNumber)
                }
            }
        })
        record(result, workplaceId)
    }

    override suspend fun confirmOhs(workplaceId: String, part: OhsConfirmation): Result<OhsRecord> = callable {
        record(functions.call("confirmOhsTraining", buildJsonObject {
            put("workplaceId", workplaceId)
            put("part", part.name)
        }), workplaceId)
    }

    override suspend fun ohsDocument(workplaceId: String, userId: String, kind: OhsDocumentKind): Result<OhsDocumentLink> = callable {
        val result = functions.call("ohsTrainingDocument", buildJsonObject {
            put("workplaceId", workplaceId)
            put("userId", userId)
            put("kind", kind.name)
        }).jsonObject
        OhsDocumentLink(
            url = result["url"]?.jsonPrimitive?.contentOrNull?.takeIf { it.isNotEmpty() } ?: error("The document has no link"),
            fileName = result["fileName"]?.jsonPrimitive?.contentOrNull.orEmpty()
        )
    }

    override suspend fun getTrainingStatus(workplaceId: String, userId: String): Result<TrainingStatus?> = runCatching {
        workplace(workplaceId).collection("training_status").document(userId).get().takeIf { it.exists }
            ?.let { CourseMapper.toDomain(it.data<TrainingStatusDto>(), userId, workplaceId) }
    }

    override suspend fun getTrainingStatuses(workplaceId: String): Result<List<TrainingStatus>> = runCatching {
        workplace(workplaceId).collection("training_status").get().documents
            .map { CourseMapper.toDomain(it.data<TrainingStatusDto>(), it.id, workplaceId) }
    }

    private fun record(element: JsonElement, workplaceId: String): OhsRecord {
        val dto = decode<OhsRecordDto>(element)
        return CourseMapper.toDomain(dto, dto.userId, workplaceId)
    }

    /** Runs a callable, turning the server's refusals (details.reason) into [CourseActionException]. */
    private inline fun <T> callable(block: () -> T): Result<T> = try {
        Result.success(block())
    } catch (e: CloudFunctionsException) {
        Result.failure(failureOf(e) ?: e)
    } catch (e: Exception) {
        Result.failure(e)
    }

    companion object {
        private val json = Json { ignoreUnknownKeys = true; coerceInputValues = true; explicitNulls = false }

        inline fun <reified T> decode(element: JsonElement): T = jsonForDecoding().decodeFromJsonElement(element)

        @PublishedApi
        internal fun jsonForDecoding(): Json = json

        /** The refusal the server named, if it is one the app explains. */
        fun failureOf(e: CloudFunctionsException): CourseActionException? {
            val reason = (e.details as? JsonObject)?.get("reason")?.jsonPrimitive?.contentOrNull
            return CourseFailure.entries.firstOrNull { it.name == reason }?.let { CourseActionException(it) }
        }
    }
}
